import React, { FC, useMemo, useState } from 'react';
import { PlusIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { Cart } from 'shared/types/cart';
import { Variant } from 'shared/types/product';
import { ExtendedProductListingProduct } from 'shared/types/product/Product';
import { Wishlist } from 'shared/types/wishlist';
import Image from 'components/commercetools-ui/atoms/image';
import Link from 'components/commercetools-ui/atoms/link';
import QuickView from 'components/commercetools-ui/organisms/product/product-quick-view';
import PackageIcon from 'components/icons/package';
import ShippingIcon from 'components/icons/shipping';
import { useFormat } from 'helpers/hooks/useFormat';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { getVariantLeadTime } from 'helpers/utils/getVariantLeadTime';
import { replaceTemplateString } from 'helpers/utils/replaceTemplateString';
import { desktop } from 'helpers/utils/screensizes';
import useColorOptions from './hooks/useColorOptions';
import useProductPricing from './hooks/useProductPricing';
import Prices from './prices';

export interface ProductTileProps {
  product: ExtendedProductListingProduct;
  isSearchResult?: boolean;
  disableQuickView?: boolean;
  disableWishlistButton?: boolean;
  disableVariants?: boolean;
  selectedVariantIndex?: number;
  wishlist?: Wishlist;
  cart?: Cart;
  onClick?: () => void;
  onAddToCart?: (variant: Variant, quantity: number) => Promise<void>;
}

const ProductTile: FC<ProductTileProps> = ({
  product,
  isSearchResult = false,
  disableQuickView = false,
  disableWishlistButton = false,
  disableVariants = false,
  selectedVariantIndex = 0,
  wishlist,
  cart,
  onClick,
  onAddToCart,
}) => {
  const [isDesktopSize] = useMediaQuery(desktop);
  const { formatMessage: formatProductMessage } = useFormat({ name: 'product' });

  // Use the pricing hook to handle all price-related logic
  const {
    displayPrice,
    displayDiscountedPrice,
    hasDiscount,
    discountPercentage,
    isStartingPrice,
    isSaleStartingPrice,
    shouldHidePrice,
  } = useProductPricing(product, selectedVariantIndex);

  // State for selected finish/color
  const [selectedColor, setSelectedColor] = useState<string | undefined>(undefined);

  // Use a default variant for display purposes
  const selectedVariant = product?.variants[selectedVariantIndex];

  const [imageHovered, setImageHovered] = useState(false);
  const [buttonHovered, setButtonHovered] = useState(false);
  const buttonIsVisible = useMemo(
    () => (imageHovered || buttonHovered) && isDesktopSize,
    [imageHovered, buttonHovered, isDesktopSize],
  );

  const hideButton = () => {
    setImageHovered(false);
    setButtonHovered(false);
  };

  const productUrl = useMemo(() => {
    if (!product._url) return `/${(product.name ?? '').toLowerCase().replace(/\s+/g, '-')}/p/${selectedVariant.sku}`;

    const name = product._url.split('/')[1];

    return `/${name}/p/${selectedVariant.sku}` + (isSearchResult ? '?sr=1' : '');
  }, [product, selectedVariant, isSearchResult]);

  // Use the custom hook to get color options
  const colorOptions = useColorOptions(product);

  // Determine the currently selected color/finish name
  const displayedColorName = useMemo(() => {
    if (selectedColor) {
      return selectedColor;
    }

    return colorOptions.length > 0 ? colorOptions[0].name : '';
  }, [selectedColor, colorOptions]);

  const leadTime = getVariantLeadTime(selectedVariant);

  // Determine stock status - consider product in stock if any variant is in stock
  // We're doing this since we don't change the selected variant in the card,
  // but that it can change in the future so we use the actual selected variant's value.
  const isInStock = useMemo(() => {
    return product.variants.some((variant) => variant.isOnStock !== false);
  }, [product.variants]);

  const businessDaysText = formatProductMessage({
    id: 'businessDays',
    defaultMessage: '{leadTime} business day(s)',
  });

  const formattedBusinessDays = replaceTemplateString(businessDaysText, { leadTime });

  // Determine if we should show colors section
  const showColorsSection = colorOptions.length > 0;

  // Limit displayed swatches to maximum of 5
  const displayedColorOptions = colorOptions.slice(0, 5);
  const hasMoreColors = colorOptions.length > 5;

  // Determine the label for the color/finish section
  const colorSectionLabel = product.colorsMap
    ? formatProductMessage({ id: 'color', defaultMessage: 'Color' })
    : formatProductMessage({ id: 'finish', defaultMessage: 'Finish' });

  return (
    <div onClick={onClick} data-testid="product-tile" className="border-2 border-stroke-primary">
      <div className="relative">
        <Link link={productUrl}>
          <div
            className="relative w-full"
            data-testid="image-container"
            onMouseEnter={() => setImageHovered(true)}
            onMouseLeave={() => setImageHovered(false)}
          >
            <div className="relative flex h-[17.5rem] w-full items-center justify-center bg-white md:h-[19rem] lg:h-[26.25rem]">
              <div className="relative size-full max-w-[19.4375rem] md:max-w-[19rem] lg:max-w-[38rem]">
                <Image
                  src={selectedVariant.images?.[0]}
                  alt={product.name ?? ''}
                  style={{
                    objectFit: 'contain',
                    objectPosition: 'center',
                  }}
                  height={800}
                  scaleBy={'height'}
                  className="size-full group-hover:opacity-75"
                  fill
                />
              </div>
            </div>
          </div>
        </Link>

        <div
          className="absolute bottom-0 z-10 w-full"
          onMouseEnter={() => setButtonHovered(true)}
          onMouseLeave={() => setButtonHovered(false)}
        >
          {hasDiscount && discountPercentage > 0 && (
            <div className="w-full text-center">
              <p className="body-text-x-small mb-8 ml-8 flex h-24 w-42 items-center justify-center bg-feedback-success text-text-inverted">
                {Math.round(discountPercentage)}%
              </p>
            </div>
          )}
          {/* If we want to implement the quickView in the future, we should adapt it to work like the PDP */}
          {/* <QuickView
            buttonIsVisible={buttonIsVisible && !disableQuickView}
            product={product}
            wishlist={wishlist}
            cart={cart}
            hideButton={hideButton}
            onAddToCart={async (variant, quantity: number) => {
              await onAddToCart?.(variant, quantity);
            }}
          /> */}
        </div>
      </div>

      <div className="flex flex-col p-16">
        {/* Product Name */}
        <h3 className="body-text-large block font-medium">{product?.name}</h3>

        {/* Price - only shown if price is not 0 or null */}
        {!shouldHidePrice && (
          <div className="mt-12">
            <Prices
              price={displayPrice}
              discountedPrice={displayDiscountedPrice}
              isStartingPrice={isStartingPrice}
              isSaleStartingPrice={isSaleStartingPrice}
            />
          </div>
        )}

        {/* Color/Finish Information - only shown if colors exist */}
        {showColorsSection && (
          <p className="body-text-small mt-12 font-regular">
            {colorSectionLabel}: <span className="font-medium">{displayedColorName}</span>
          </p>
        )}

        {/* Color Swatches - only shown if finishes exist */}
        {!disableVariants && showColorsSection && (
          <div className="mt-12 flex flex-wrap items-center gap-8">
            {displayedColorOptions.map((colorOption, index) => (
              <button
                key={index}
                data-testid="product-finish"
                className={`block size-36 cursor-pointer border ${
                  selectedColor === colorOption.name || (!selectedColor && index === 0)
                    ? 'border-2 border-stroke-secondary'
                    : ''
                }`}
                style={{
                  backgroundColor: colorOption.color || '#CCCCCC',
                }}
                onClick={(e) => {
                  e.preventDefault();
                  setSelectedColor(colorOption.name);
                }}
              ></button>
            ))}

            {/* Show "+" button if there are more colors */}
            {hasMoreColors && (
              <Link link={productUrl}>
                <span className="flex size-36 cursor-pointer items-center justify-center font-medium">
                  <PlusIcon />
                </span>
              </Link>
            )}
          </div>
        )}

        {/* Shipping Information */}
        <div className="mt-12 flex items-center gap-8">
          <ShippingIcon />
          <p className="body-text-x-small ml-1 font-regular leading-[18px] text-text-tertiary">
            {formatProductMessage({ id: 'shipsInAbout', defaultMessage: 'Ships in about' })}{' '}
            <span className="font-medium">{formattedBusinessDays}</span>
          </p>
        </div>

        {/* Stock Status */}
        {shouldHidePrice ? (
          /* Product unavailable message when price is hidden */
          <div className="mt-8 flex items-center gap-8 font-medium">
            <ExclamationCircleIcon className="size-16 stroke-feedback-error" strokeWidth={1.5} />
            <p className="body-text-x-small ml-1 text-feedback-error">
              {formatProductMessage({ id: 'productUnavailable', defaultMessage: 'Product currently unavailable' })}
            </p>
          </div>
        ) : (
          /* Regular stock status when price is shown */
          <div className="mt-8 flex items-center gap-8">
            <PackageIcon className={isInStock ? 'stroke-feedback-success' : 'stroke-feedback-error'} />
            <p className={`body-text-x-small ml-1 ${isInStock ? 'text-feedback-success' : 'text-feedback-error'}`}>
              {isInStock
                ? formatProductMessage({ id: 'inStock', defaultMessage: 'In stock' })
                : formatProductMessage({ id: 'outOfStock', defaultMessage: 'Out of stock' })}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductTile;
